/*
 * Per-flow entry capture (sessionStorage) — the refresh-survival rail.
 *
 * Extracted from WaiverFlow's rememberWaiverTicket/waiverTicketFromSession
 * (BOARDROOM-174911): React Router DROPS location.state on a hard refresh, and
 * a deep link never has it — so a flow that keys anything off its entry route
 * state (the my-api ticket target, the guardian child-framing) loses it on
 * reload. Each flow captures its entry the moment route state carries it,
 * keyed per flow + event, and recovers it on a mount without route state.
 *
 * Shared by BOTH the waiver flow (ticketId — the server submit target) and the
 * questionnaire flow (ticketId + guardian framing — display-only banner
 * survival). Device-session-local, honest about its limits: never presented as
 * a server write, and a genuinely fresh session (new tab, other device) has no
 * record — flows degrade to their pre-capture behaviour, never invent.
 */

export interface FlowEntryFraming {
  /** The entry holding's backing ticket — the join key to holdings/readiness. */
  ticketId: string
  /** Get-ready's per-holding subject carry (holderKind 'child' ⇒ a parent/
   *  guardian is acting for the named child; 'self' ⇒ the wire says the seat
   *  is the caller's own). Absent when the entry carried none. */
  holderKind?: string
  holderName?: string | null
  /** The server's /me/holdings childContext mark for the entry ticket
   *  (questionnaireSubjectTicket input) — survives a reload so a fresh-device
   *  guardian seat keeps keying on the child. Absent when unknown. */
  childContext?: boolean
}

const entryKey = (flow: string, eventId: string): string => `mytrumin.${flow}.ticket.${eventId}`

export function rememberFlowEntry(flow: string, eventId: string, framing: FlowEntryFraming): void {
  try {
    sessionStorage.setItem(entryKey(flow, eventId), JSON.stringify(framing))
  } catch {
    /* private-mode / quota — the flow degrades to route-state-only, never throws */
  }
}

export function flowEntryFromSession(flow: string, eventId: string): FlowEntryFraming | null {
  try {
    const raw = sessionStorage.getItem(entryKey(flow, eventId))
    if (!raw) return null
    // Legacy tolerance: pre-extraction waiver entries stored the BARE ticketId
    // string under the same key — still honoured within a live session.
    if (!raw.startsWith('{')) return { ticketId: raw }
    const parsed: unknown = JSON.parse(raw)
    if (
      parsed && typeof parsed === 'object'
      && typeof (parsed as { ticketId?: unknown }).ticketId === 'string'
      && (parsed as { ticketId: string }).ticketId !== ''
    ) {
      return parsed as FlowEntryFraming
    }
    return null
  } catch {
    return null
  }
}
